import React, { useEffect, useRef, useState } from 'react';
import { BOARD_SIZE } from '../constants';
import '../styles/animations.css';
import { gameStyles } from '../styles/gameStyles';

const Board = ({
  board,
  walls,
  onCellClick,
  onWallClick,
  selectedStone,
  turnAction,
  currentPlayer,
  possibleMoves = [],
  possibleWalls = [],
  separatedCellsGroups = null,
  gameOver = false,
  currentStyle = 'classic',
}) => {
  const PADDING = 16; // px, matches Tailwind p-4
  const WALL_THICKNESS = 3;
  const WALL_OFFSET = WALL_THICKNESS / 2;
  const styles = gameStyles[currentStyle];
  const boardRef = useRef(null);
  const [boardSize, setBoardSize] = useState(0);

  // Convert possibleMoves to a Set for fast lookup
  const possibleMovesSet = new Set(possibleMoves.map(m => `${m.row},${m.col}`));

  // Calculate cell size based on board size
  const cellSize = boardSize / BOARD_SIZE;

  // Update size on mount and window resize
  useEffect(() => {
    const updateSize = () => {
      const container = boardRef.current;
      if (!container) return;

      const parentWidth = container.parentElement.clientWidth;
      const parentHeight = container.parentElement.clientHeight;

      // Calculate the maximum board size that fits in the container
      const maxSize = Math.min(
        parentWidth - 2 * PADDING,
        parentHeight - 2 * PADDING
      );

      // Set a minimum board size to ensure the board remains playable
      const newSize = Math.max(32 * BOARD_SIZE, Math.floor(maxSize));
      setBoardSize(newSize);
    };

    // Initial size calculation
    updateSize();

    // Listen for window resize events
    window.addEventListener('resize', updateSize);

    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  // Find the group that contains a specific cell
  const findGroupForCell = (row, col) => {
    if (!separatedCellsGroups) return null;

    return separatedCellsGroups.find(group =>
      group.cells.some(cell => cell.row === row && cell.col === col)
    );
  };

  // Find if this is the first stone in a group
  const isFirstStoneInGroup = (row, col, group) => {
    if (!group || !group.cells || !group.player) return false;

    const firstStoneCell = group.cells.find(({ stone }) => stone)

    return firstStoneCell.row === row && firstStoneCell.col === col;
  };

  const renderCell = (row, col) => {
    const cell = board[row][col];
    const isSelected = selectedStone && selectedStone.row === row && selectedStone.col === col;
    const isPossibleMove = possibleMovesSet.has(`${row},${col}`);
    const isEmpty = cell === null;
    const group = findGroupForCell(row, col);
    const isFirstStone = isFirstStoneInGroup(row, col, group);

    // During placing phase, only prevent clicks on occupied cells
    // During moving phase, allow clicks on current player's stones and empty cells
    const isDisabled = gameOver || (turnAction === 'place' ? cell !== null : cell !== null && cell !== currentPlayer)

    // Determine background color based on group status
    let bgColor = '';
    if (group?.settled) {
      if (group.player) {
        bgColor = group.player === 'black' ? styles.settledFirstStoneCell : styles.settledSecondStoneCell;
      } else {
        bgColor = styles.settledNeutralCell;
      }
    }

    return (
      <div
        key={`cell-${row}-${col}`}
        className={`
          relative
          ${!isDisabled && 'cursor-pointer'}
          ${(isPossibleMove || (turnAction === 'place' && isEmpty)) && `${styles.cellHover} hover:bg-opacity-50 transition-colors duration-200`}
          ${bgColor}
        `}
        style={{
          width: `${cellSize}px`,
          height: `${cellSize}px`
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (!isDisabled) {
            onCellClick(row, col);
          }
        }}
      >
        {cell && (
          <div className={`
            absolute inset-3 rounded-full
            ${cell === 'black'
              ? styles.firstStoneShadow
              : styles.secondStoneShadow
            }
            ${isSelected && `${styles.selectedStone} ring-2 ring-offset-2`}
            ${!isDisabled && 'transform hover:scale-105 transition-all duration-200 hover:shadow-[0_4px_8px_rgba(0,0,0,0.3)]'}
            animate-[popIn_0.3s_ease-out]
          `}>
            {isFirstStone && group?.settled && group?.points > 0 && (
              <div className={`
                absolute inset-0 flex items-center justify-center
                font-bold select-none
                text-xs sm:text-lg
                ${cell === 'black' ? `${styles.firstStonePoints}` : `${styles.secondStonePoints}`}
              `}>
                {group.points}
              </div>
            )}
          </div>
        )}
        {isPossibleMove && (
          <div className={`absolute inset-4 rounded-full border-2 ${styles.possibleMove}`} />
        )}
      </div>
    );
  };

  const renderWalls = () => {
    const wallElements = [];

    // Render horizontal walls
    for (let row = 0; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        if (walls.horizontal[row][col]) {
          wallElements.push(
            <div
              key={`h-wall-${row}-${col}`}
              className={`
                absolute rounded
                ${walls.horizontal[row][col] === 'black' ? styles.firstPlayerWall : styles.secondPlayerWall}
                animate-[popIn_0.3s_ease-out]
              `}
              style={{
                left: `${col * cellSize + PADDING + 1}px`,
                top: `${row * cellSize + PADDING - WALL_OFFSET + 1}px`,
                width: `${cellSize}px`,
                height: `${WALL_THICKNESS}px`,
              }}
            />
          );
        }
      }
    }

    // Render vertical walls
    for (let row = 0; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        if (walls.vertical[row][col]) {
          wallElements.push(
            <div
              key={`v-wall-${row}-${col}`}
              className={`
                absolute rounded
                ${walls.vertical[row][col] === 'black' ? styles.firstPlayerWall : styles.secondPlayerWall}
                animate-[popIn_0.3s_ease-out]
              `}
              style={{
                left: `${col * cellSize + PADDING - WALL_OFFSET + 1}px`,
                top: `${row * cellSize + PADDING + 1}px`,
                width: `${WALL_THICKNESS}px`,
                height: `${cellSize}px`,
              }}
            />
          );
        }
      }
    }

    return wallElements;
  };

  const renderWallPlacementButtons = () => {
    if (!selectedStone) return null;

    return possibleWalls.map(config => (
      <button
        key={`${config.type}-${config.row}-${config.col}-wall`}
        className={`absolute rounded-full ${styles.possibleWall} transition-colors duration-200 z-30`}
        style={{
          left: config.type === 'vertical'
            ? `${config.col * cellSize + PADDING - WALL_OFFSET * 3 + 1}px`
            : `${config.col * cellSize + PADDING + 1}px`,
          top: config.type === 'horizontal'
            ? `${config.row * cellSize + PADDING - WALL_OFFSET * 3 + 1}px`
            : `${config.row * cellSize + PADDING + 1}px`,
          width: config.type === 'vertical' ? `${WALL_THICKNESS * 3}px` : `${cellSize}px`,
          height: config.type === 'horizontal' ? `${WALL_THICKNESS * 3}px` : `${cellSize}px`
        }}
        onClick={(e) => {
          e.stopPropagation();
          onWallClick(config.type, config.row, config.col);
        }}
      />
    ));
  };

  const renderGridLines = () => {
    const lines = [];

    // Vertical lines
    for (let col = 0; col <= BOARD_SIZE; col++) {
      lines.push(
        <div
          key={`v-grid-${col}`}
          className="absolute pointer-events-none"
          style={{
            left: `${col * cellSize + PADDING}px`,
            top: `${PADDING}px`,
            width: '2px',
            height: `${boardSize}px`,
            backgroundColor: styles.cellBorder
          }}
        />
      );
    }
    // Horizontal lines
    for (let row = 0; row <= BOARD_SIZE; row++) {
      lines.push(
        <div
          key={`h-grid-${row}`}
          className="absolute pointer-events-none"
          style={{
            top: `${row * cellSize + PADDING}px`,
            left: `${PADDING}px`,
            height: '2px',
            width: `${boardSize}px`,
            backgroundColor: styles.cellBorder
          }}
        />
      );
    }
    return lines;
  };

  return (
    <div
      className="h-full flex items-center justify-center"
      ref={boardRef}
      style={{
        minWidth: 0,
        minHeight: 0
      }}
    >
      <div
        className="relative rounded-lg p-4"
        style={{
          width: `${boardSize + 2 * PADDING}px`,
          height: `${boardSize + 2 * PADDING}px`,
          backgroundColor: styles.boardBackground,
          boxShadow: styles.boardShadow,
          maxWidth: '100%',
          maxHeight: '100%',
          minWidth: 0,
          minHeight: 0
        }}
      >
        {/* Grid cells - always visible, but only interactive during place and move phases */}
        <div
          className="grid gap-0"
          style={{
            gridTemplateColumns: `repeat(${BOARD_SIZE}, minmax(0, 1fr))`,
            position: 'absolute',
            top: `${PADDING}px`,
            left: `${PADDING}px`,
            width: `${boardSize}px`,
            height: `${boardSize}px`
          }}
        >
          {Array.from({ length: BOARD_SIZE }, (_, row) =>
            Array.from({ length: BOARD_SIZE }, (_, col) => renderCell(row, col))
          )}
        </div>

        {/* Walls - always visible, now above grid lines and cells */}
        <div className="absolute inset-0 z-20 pointer-events-none">
          {renderWalls()}
        </div>

        {/* Wall placement buttons - only show during wall phase */}
        {turnAction === 'wall' && (
          <div className="absolute inset-0">
            {renderWallPlacementButtons()}
          </div>
        )}

        {/* Grid lines - always visible, now below walls */}
        <div className="absolute inset-0 z-10 pointer-events-none">
          {renderGridLines()}
        </div>
      </div>
    </div>
  );
};

export default Board;
